import { supabase } from "@/lib/supabase";
import type { FollowUpTask } from "@/data/opportunityTypes";

// ── DB row shape ──────────────────────────────────────────────────────────────

export interface FollowUpTaskRow {
  id: string;                    // UUID PK
  task_id: string;               // human-readable ID (used as UI id)
  lead_id: string | null;
  quote_id: string | null;
  disposition_rule_id: string | null;
  task_type: string;             // thank_you | follow_up | reminder | escalation | final
  status: string;                // pending | scheduled | in_progress | completed | cancelled | failed
  priority: string;              // low | medium | high | urgent
  subject: string;               // customer name / subject line
  description: string | null;
  due_date: string;
  scheduled_at: string | null;
  completed_at: string | null;
  completed_by: string | null;
  execution_method: string | null; // call | text | call_and_tetx | email
  contact_phone: string | null;
  contact_email: string | null;
  notes: string | null;
  created_by: string;
  created_at: string | null;
  updated_at: string | null;
}

// ── DB → UI ───────────────────────────────────────────────────────────────────

function toUiStatus(dbStatus: string): "pending" | "completed" | "overdue" {
  if (dbStatus === "completed") return "completed";
  return "pending"; // pending | scheduled | in_progress | cancelled | failed → pending
}

function toUiType(taskType: string): "rep" | "manager" | "none" {
  if (taskType === "escalation") return "manager";
  return "rep";
}

function toUiMethod(method: string | null): "call" | "text" | "call_and_text" | undefined {
  if (!method) return undefined;
  if (method === "call_and_tetx") return "call_and_text"; // fix DB typo
  if (method === "email") return "call"; // map email → call as closest fit
  return method as "call" | "text";
}

export function toUiFollowUpTask(row: FollowUpTaskRow): FollowUpTask {
  return {
    id: row.task_id,
    opportunityId: row.lead_id ?? row.quote_id ?? "",
    type: toUiType(row.task_type),
    assignedTo: row.created_by,
    dueDate: row.due_date,
    status: toUiStatus(row.status),
    notes: row.notes ?? row.description ?? "",
    createdAt: row.created_at ?? new Date().toISOString(),
    disposition: {} as any,
    customerName: row.subject,
    customerPhone: row.contact_phone ?? "",
    dispositionReason: row.disposition_rule_id ?? undefined,
    executionMethod: toUiMethod(row.execution_method),
    priority: (row.priority === "urgent" ? "high" : row.priority) as "high" | "medium" | "low" | undefined,
    completedAt: row.completed_at ?? undefined,
  };
}

// ── UI → DB ───────────────────────────────────────────────────────────────────

function toDbStatus(uiStatus: string): string {
  if (uiStatus === "completed") return "completed";
  return "pending";
}

function toDbRow(task: FollowUpTask): Omit<FollowUpTaskRow, "id" | "created_at" | "updated_at"> {
  return {
    task_id: task.id,
    lead_id: task.opportunityId || null,
    quote_id: null,
    disposition_rule_id: task.dispositionReason ?? null,
    task_type: task.type === "manager" ? "escalation" : "follow_up",
    status: toDbStatus(task.status),
    priority: task.priority ?? "medium",
    subject: task.customerName,
    description: task.notes || null,
    due_date: task.dueDate,
    scheduled_at: null,
    completed_at: task.completedAt ?? null,
    completed_by: task.completedAt ? task.assignedTo : null,
    execution_method: task.executionMethod === "call_and_text" ? "call_and_tetx" : task.executionMethod ?? null,
    contact_phone: task.customerPhone || null,
    contact_email: null,
    notes: task.notes || null,
    created_by: task.assignedTo,
  };
}

// ── Service ───────────────────────────────────────────────────────────────────

export const FollowUpTaskService = {
  async getAll(): Promise<{ data: FollowUpTask[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("follow_up_tasks")
      .select("*")
      .not("status", "in", '("cancelled","failed")')
      .order("due_date", { ascending: true });

    if (error || !data) return { data: null, error };
    return { data: (data as FollowUpTaskRow[]).map(toUiFollowUpTask), error: null };
  },

  async upsert(task: FollowUpTask): Promise<{ error: unknown }> {
    const row = toDbRow(task);
    const { error } = await supabase
      .from("follow_up_tasks")
      .upsert(row, { onConflict: "task_id" });
    if (error) console.error("[FollowUpTaskService.upsert] error:", error);
    return { error };
  },

  async updateStatus(
    taskId: string,
    status: string,
    completedAt?: string,
    completedBy?: string,
  ): Promise<{ error: unknown }> {
    const patch: Partial<FollowUpTaskRow> = { status };
    if (completedAt) patch.completed_at = completedAt;
    if (completedBy) patch.completed_by = completedBy;

    const { error } = await supabase
      .from("follow_up_tasks")
      .update(patch)
      .eq("task_id", taskId);
    if (error) console.error("[FollowUpTaskService.updateStatus] error:", error);
    return { error };
  },
};
